// Foundation tab — the statement upload control (PR 4, 2026-09-18). A hidden
// file input behind a button; the chosen .csv or .xlsx goes to
// POST /api/foundation/statements as multipart/form-data and the result is
// reported in words: rows read, credits, how many were duplicates of a
// statement already held (not counted), how many credits are unmatched.
import { useRef, useState } from 'react'

interface UploadResult {
  statement_id: number; file_name: string; period_from: string; period_to: string
  rows: number; credits: number; credit_total: number; debits: number; debit_total: number
  duplicates: number; counted_credits: number; counted_total: number; unmatched: number
  account: { sort_code: string | null; account_number: string | null; alias: string | null } | null
  warnings: string[]
}

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
const fmt = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`

export function StatementUpload({ onUploaded }: { onUploaded: (message: string) => void }) {
  const input = useRef<HTMLInputElement | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<UploadResult | null>(null)

  const upload = async (file: File) => {
    setBusy(true); setError(null); setResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file, file.name)
      fd.append('name', file.name)
      const r = await fetch('/api/foundation/statements', { method: 'POST', credentials: 'include', body: fd })
      const j = (await r.json().catch(() => ({}))) as UploadResult & { error?: string }
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      setResult(j)
      const dupNote = j.duplicates === j.rows
        ? 'every row repeats a statement already held, so nothing was added to the totals'
        : j.duplicates > 0 ? `${j.duplicates} repeat a statement already held and are not counted` : 'no duplicates'
      onUploaded(`Loaded "${j.file_name}": ${j.rows} rows, ${fmt(j.period_from)} to ${fmt(j.period_to)}; ${dupNote}; ${j.counted_credits} credits counted (${gbp.format(j.counted_total)}), ${j.unmatched} unmatched.`)
    } catch (e) {
      setError(String((e as Error)?.message ?? e))
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div className="fnd-section">
      <div className="fnd-toolbar">
        <div>
          <h3 style={{ margin: 0 }}>Upload a statement</h3>
          <p className="fnd-note">The Foundation Fund account&rsquo;s Santander export, .csv as downloaded or the same rows pasted into an .xlsx. Rows already held from an earlier statement are recognised and not counted twice.</p>
        </div>
        <span className="fnd-spacer" />
        <input ref={input} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f) }} />
        <button type="button" className="btn-primary" disabled={busy} onClick={() => input.current?.click()}>{busy ? 'Loading…' : 'Upload a statement (.csv or .xlsx)'}</button>
      </div>
      {error && <p className="fnd-error">{error}</p>}
      {result && (
        <div className="fnd-upload-result">
          <p><strong>{result.file_name}</strong> — {result.rows} rows from {fmt(result.period_from)} to {fmt(result.period_to)}{result.account?.account_number ? `, account ${result.account.sort_code ?? ''} ${result.account.account_number}${result.account.alias ? ` ${result.account.alias}` : ''}` : ''}.</p>
          <ul>
            <li>{result.credits} credits {gbp.format(result.credit_total)}, {result.debits} debits {gbp.format(result.debit_total)} in the file.</li>
            <li>{result.duplicates === 0 ? 'No row repeats a statement already held.' : result.duplicates === result.rows ? 'Every row repeats a statement already held: shown on this statement, counted nowhere.' : `${result.duplicates} rows repeat a statement already held and are not counted.`}</li>
            <li>{result.counted_credits} credits counted ({gbp.format(result.counted_total)}); {result.unmatched === 0 ? 'all matched to members.' : `${result.unmatched} unmatched — assign them on the Bank payments view.`}</li>
            {result.warnings.map((w) => <li key={w} className="fnd-error">{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  )
}
